import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { Stack, MenuItem, Box, Typography, useTheme } from '@mui/material';
import { PATH_AUTH, PATH_APP } from 'src/routes/paths';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import MenuPopover from 'src/components/MenuPopover';
import useAuth from 'src/hooks/useAuth';
import { showUserFullName } from 'src/utils/showUserFullName';
import { RoleNavbar } from 'src/layouts/dashboard/navbar/Navbar';
import { ListTeamByAdminGroupQuery, Team } from 'src/API';
import { CustomAvatar } from 'src/components/CustomAvatar';
import { gql } from '@apollo/client';
import { awsApiAdapter } from 'src/utils/awsApiAdapter';
import * as queries from 'coteam4/src/graphql/queries';
import { nonNullable } from 'src/utils/typeUtil';
import { useOrganization } from 'src/contexts/OrganizationContext';

type Props = {
  role: RoleNavbar;
};

export const AccountPopover = ({ role }: Props) => {
  const router = useRouter();
  const theme = useTheme();

  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const { logout } = useAuth();
  const { organization, currentUser, isLoading } = useOrganization();
  const [teams, setTeams] = useState<Team[]>([]);
  const [open, setOpen] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // currentUserがない場合はfetchしない
    if (!currentUser?.adminGroup) return;

    const fetchTeams = async () => {
      const result = await awsApiAdapter.graphqlQuery<ListTeamByAdminGroupQuery>({
        query: gql(queries.listTeamByAdminGroup as any) as any,
        variables: {
          adminGroup: currentUser.adminGroup,
        },
      });
      setTeams(result.data?.listTeamByAdminGroup?.items.filter(nonNullable) as Team[]);
    };
    fetchTeams();
  }, [currentUser?.adminGroup]);

  const fullName = useMemo(() => showUserFullName(currentUser), [currentUser]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      router.replace(PATH_AUTH.login);

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  // ローディング中またはデータがない場合は何も表示しない
  if (isLoading || !organization || !currentUser) {
    return null;
  }

  return (
    <>
      <Box py="12px" borderTop={`1px solid ${theme.palette.grey[300]}`}>
        <Box
          sx={{
            borderRadius: '4px',
            border: role === 'admin' ? `0.7px solid ${theme.palette.secondary.main}` : '',
            width: '100%',
            ':hover': {
              cursor: 'pointer',
              backgroundColor: role === 'member' ? theme.palette.background.secondary : '',
              borderColor: role === 'admin' ? theme.palette.border.primary : '',
            },
          }}
        >
          <div onClick={handleOpen}>
            <Stack direction="row" alignItems="center" py="8px" px="4px" gap="8px">
              <CustomAvatar
                id={currentUser.id}
                alt={fullName}
                src={currentUser?.thumbnailPhotoUrl ?? ''}
                isLink={false}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 12,
                }}
              />
              <Stack
                sx={{
                  color:
                    role === 'admin' ? theme.palette.text.contrast : theme.palette.text.primary,
                  width: '121px',
                }}
              >
                <Typography
                  variant="titleXS"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  whiteSpace="nowrap"
                >
                  {fullName}
                </Typography>

                <Typography
                  variant="textS"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                >
                  {teams?.find((team) => team.id === currentUser?.teamID)?.name ?? '　'}
                </Typography>
              </Stack>
            </Stack>
          </div>
        </Box>
      </Box>
      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mb: 3,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <NextLink
          href={{
            pathname: PATH_APP.me.setting.profile,
            query: { organizationName: organization.name },
          }}
          passHref
          legacyBehavior
        >
          <MenuItem sx={{ m: 1 }} onClick={handleClose}>
            プロフィール表示
          </MenuItem>
        </NextLink>
        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          ログアウト
        </MenuItem>
      </MenuPopover>
    </>
  );
};
